import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { DatePipe, NgClass, NgForOf, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault } from '@angular/common';
import { RouterLink } from '@angular/router';
import {
  ClrDatagridFilterInterface,
  ClrDatagridModule,
  ClrDatagridSortOrder,
  ClrDatagridStringFilterInterface,
  ClrIconModule,
  ClrLoadingModule,
  ClrModalModule,
} from '@clr/angular';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { Subject } from 'rxjs';
import { fileIcon, ClarityIcons } from '@cds/core/icon';

import { Warehouse } from '../../../../_shared/interfaces';
import { WarehouseStoreService } from '../../../../_shared/services';
import { filesBaseUrl } from '../../../../_shared/utils/rest-url';
import { getTenantStorage } from '../../../../_shared/utils/tenant-context';

export interface MovementTypeOption {
  value: Warehouse['movement_type'];
  label: string;
}

export class MovementTypeFilter implements ClrDatagridFilterInterface<Warehouse> {
  changes = new Subject<void>();
  selected = new Set<string>();

  readonly options: MovementTypeOption[] = [
    { value: 'stock_init',        label: 'Lager initialisiert' },
    { value: 'incoming',          label: 'Wareneingang' },
    { value: 'outgoing',          label: 'Warenausgang' },
    { value: 'internal_transfer', label: 'Interne Umbuchung' },
    { value: 'return_inbound',    label: 'Rückgabe Eingang' },
    { value: 'return_outbound',   label: 'Rückgabe Ausgang' },
    { value: 'scrap',             label: 'Ausschuss' },
    { value: 'provision_in',      label: 'Beistellung Eingang' },
    { value: 'provision_out',     label: 'Beistellung Ausgang' },
    { value: 'inventory_in',      label: 'Inventur (Zugang)' },
    { value: 'inventory_out',     label: 'Inventur (Abgang)' },
  ];

  isActive(): boolean { return this.selected.size > 0; }
  accepts(item: Warehouse): boolean { return !this.isActive() || this.selected.has(item.movement_type); }
  toggle(value: string): void {
    this.selected.has(value) ? this.selected.delete(value) : this.selected.add(value);
    this.changes.next();
  }
}

class DrawingFilter implements ClrDatagridStringFilterInterface<Warehouse> {
  accepts(item: Warehouse, search: string): boolean {
    return String(item.technical_drawing ?? '').toLowerCase().includes(search.toLowerCase());
  }
}

@Component({
  selector: 'app-job-details-warehouse',
  standalone: true,
  imports: [
    NgIf, NgForOf, NgClass, NgSwitch, NgSwitchCase, NgSwitchDefault,
    DatePipe,
    RouterLink,
    ClrDatagridModule,
    ClrIconModule,
    ClrLoadingModule,
    ClrModalModule,
    NgxExtendedPdfViewerModule,
  ],
  templateUrl: './job-details-warehouse.component.html',
  styleUrl:    './job-details-warehouse.component.sass',
})
export class JobDetailsWarehouseComponent implements OnChanges {
  @Input() projectId: string = '';
  @Input() jobId: number = 0;

  entries: Warehouse[] = [];
  loading = true;
  descSort = ClrDatagridSortOrder.DESC;
  private readonly balances = new Map<number, number>();

  showPdf = false;
  modalUrl = '';
  modalContent = '';

  readonly movementFilter = new MovementTypeFilter();
  readonly drawingFilter  = new DrawingFilter();

  private filesDirectory = filesBaseUrl();

  constructor(private waress: WarehouseStoreService) {
    ClarityIcons.addIcons(fileIcon);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['projectId'] || changes['jobId']) && this.projectId && this.jobId > 0) {
      this.load();
    }
  }

  private load(): void {
    this.loading = true;
    this.waress.getAllWarehouses({ project_id: this.projectId, job_id: this.jobId }).subscribe({
      next:     data => { this.entries = data?.object ?? []; this.computeBalances(); },
      complete: ()   => { this.loading = false; },
      error:    ()   => { this.loading = false; },
    });
  }

  vorgangType(entry: Warehouse): 'order' | 'delivery' | 'transport' | 'component' | 'job' | 'none' {
    if (entry.delivery_id  > 0) return 'delivery';
    if (entry.transport_id > 0) return 'transport';
    if (entry.order_id     > 0) return 'order';
    if (entry.is_provision && entry.job_id > 0) return 'job';
    if (entry.movement_type === 'stock_init' && entry.technical_drawing) return 'component';
    return 'none';
  }

  private sign(type: string): 1 | -1 {
    return (type === 'outgoing' || type === 'return_outbound' || type === 'scrap'
      || type === 'provision_out' || type === 'inventory_out') ? -1 : 1;
  }

  private computeBalances(): void {
    this.balances.clear();
    const chronological = [...this.entries].sort((a, b) =>
      a.date === b.date ? a.id - b.id : (a.date < b.date ? -1 : 1)
    );
    const perDrawing = new Map<string, number>();
    for (const e of chronological) {
      if (!e.technical_drawing) continue;
      const balance = (perDrawing.get(e.technical_drawing) ?? 0) + this.sign(e.movement_type) * e.number_of_pieces;
      perDrawing.set(e.technical_drawing, balance);
      this.balances.set(e.id, balance);
    }
  }

  balanceFor(entry: Warehouse): number | null {
    return this.balances.get(entry.id) ?? null;
  }

  openPdf(fileId: string, description: string): void {
    const token = getTenantStorage('access_token');
    this.modalUrl     = `${this.filesDirectory}/download_file?file_id=${fileId}&token=${token}`;
    this.modalContent = description || 'Dokument';
    this.showPdf      = true;
  }

  movementLabel(type: string): string {
    return this.movementFilter.options.find(o => o.value === type)?.label ?? type;
  }

  pieceSign(type: string): string {
    return this.sign(type) < 0 ? '−' : '+';
  }

  pieceClass(type: string): string {
    return this.sign(type) < 0 ? 'out' : 'in';
  }

  movementClass(type: string): string {
    switch (type) {
      case 'stock_init':        return 'label label-purple';
      case 'incoming':          return 'label label-success';
      case 'outgoing':          return 'label label-danger';
      case 'internal_transfer': return 'label label-info';
      case 'return_inbound':
      case 'return_outbound':   return 'label label-warning';
      case 'provision_in':
      case 'provision_out':     return 'label label-blue';
      case 'inventory_in':      return 'label label-purple';
      case 'inventory_out':     return 'label label-purple';
      default:                  return 'label';
    }
  }

}
